import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { fetchUsers, deleteUser, createUser, fetchDownlineUsers, fetchAllDownlineUsers } from '../../../services/users';

export const fetchUsersAsync = createAsyncThunk(
    'users/fetchUsers',
    async (params = {}, { rejectWithValue }) => {
        try {
            const response = await fetchUsers(params);
            return {
                message: response.data.message,
                data: response.data.data,
                total: response.data.data.length,
                page: params.page || 1,
                limit: params.limit || 10
            };
        } catch (error) {
            return rejectWithValue(error.response?.data || 'Failed to fetch users');
        }
    }
);

export const fetchDownlineUsersAsync = createAsyncThunk(
    'users/fetchDownlineUsers',
    async ({ user_id, userType }, { rejectWithValue }) => {  // Destructure the object
        try {
            if (!user_id || !userType) {
                const response = await fetchAllDownlineUsers();
                return {
                    message: response.data.message,
                    data: response.data.data,
                };
            }
            else {
                const response = await fetchDownlineUsers(user_id, userType);
                return {
                    message: response.data.message,
                    data: response.data.data,
                };
            }
        } catch (error) {
            return rejectWithValue(error.response?.data || 'Failed to fetch downline users');
        }
    }
)

export const deleteUserAsync = createAsyncThunk(
    'users/deleteUser',
    async (userId, { rejectWithValue, dispatch }) => {
        try {
            const response = await deleteUser(userId);
            dispatch(fetchUsersAsync());
            return {
                message: response.data.message,
                userId
            };
        } catch (error) {
            return rejectWithValue(error.response?.data || 'Failed to delete user');
        }
    }
);

export const createUserAsync = createAsyncThunk(
    'users/createUser',
    async (data, { rejectWithValue, dispatch }) => {
        try {
            const response = await createUser(data);
            dispatch(fetchUsersAsync());
            return {
                message: response.data.message,
                user: response.data.data
            };
        } catch (error) {
            return rejectWithValue(error.response?.data || 'Failed to create user');
        }
    }
);

const usersSlice = createSlice({
    name: 'userDownlineList',
    initialState: {
        usersList: {
            data: [],
            total: 0,
            page: 1,
            limit: 10
        },
        loading: false,
        error: null,
        message: '',
        deletionStatus: null,
        creationStatus: null,
        createdUser: null
    },
    reducers: {
        clearUsers: (state) => {
            state.usersList.data = [];
            state.usersList.total = 0;
            state.usersList.page = 1;
            state.message = '';
            state.error = null;
        },
        clearDeletionStatus: (state) => {
            state.deletionStatus = null;
        },
        clearCreationStatus: (state) => {
            state.creationStatus = null;
            state.createdUser = null;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchUsersAsync.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchUsersAsync.fulfilled, (state, action) => {
                state.loading = false;
                state.usersList = {
                    data: action.payload.data,
                    total: action.payload.total,
                    page: action.payload.page,
                    limit: action.payload.limit
                };
                state.message = action.payload.message;
                state.error = null;
            })
            .addCase(fetchUsersAsync.rejected, (state, action) => {
                state.loading = false;
                state.usersList.data = [];
                state.error = action.payload;
            })
            .addCase(deleteUserAsync.pending, (state) => {
                state.loading = true;
                state.deletionStatus = 'loading';
                state.error = null;
            })
            .addCase(deleteUserAsync.fulfilled, (state, action) => {
                state.loading = false;
                state.deletionStatus = 'success';
                state.message = action.payload.message;
                state.error = null;
            })
            .addCase(deleteUserAsync.rejected, (state, action) => {
                state.loading = false;
                state.deletionStatus = 'error';
                state.error = action.payload;
            })
            .addCase(createUserAsync.pending, (state) => {
                state.loading = true;
                state.creationStatus = 'loading';
                state.error = null;
            })
            .addCase(createUserAsync.fulfilled, (state, action) => {
                state.loading = false;
                state.creationStatus = 'success';
                state.message = action.payload.message;
                state.createdUser = action.payload.user;
                state.error = null;
            })
            .addCase(createUserAsync.rejected, (state, action) => {
                state.loading = false;
                state.creationStatus = 'error';
                state.error = action.payload;
                state.createdUser = null;
            })

            .addCase(fetchDownlineUsersAsync.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchDownlineUsersAsync.fulfilled, (state, action) => {
                state.loading = false;
                state.usersList = {
                    data: action.payload.data,
                };
                state.message = action.payload.message;
                state.error = null;
            })
            .addCase(fetchDownlineUsersAsync.rejected, (state, action) => {
                state.loading = false;
                state.usersList.data = [];
                state.error = action.payload;
            });
    }
});

export const { clearUsers, clearDeletionStatus, clearCreationStatus } = usersSlice.actions;

export default usersSlice.reducer;